import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import "rc-slider/assets/index.css";
import "./styles/index.scss";
import "./index.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Toaster, toast } from "react-hot-toast";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { DataProvider } from "data/data-provider";
import serverURL from "api/auth/auth";
import { getCompanyID } from "api/company/company";

// Function to fetch the Stripe key from the backend
async function fetchStripeKey(companyId: number): Promise<string | null> {
  try {
    const response = await fetch(
      `${serverURL}/api/payment/publishable-key/?company_id=${companyId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const contentType = response.headers.get("content-type");
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to fetch Stripe key. Status:", response.status);
      console.error("Response Error Body:", errorText);
      return null;
    }

    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();

      if (data.publishable_key) {
        return data.publishable_key;
      } else {
        console.error("publishable_key is undefined in the API response.");
        return null;
      }
    } else {
      const errorText = await response.text();
      console.error("Unexpected content type:", contentType);
      console.error("Response Error Body:", errorText);
      return null;
    }
  } catch (error) {
    console.error("Error fetching Stripe key:", error);
    return null;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const RootComponent = () => {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeStripe = async () => {
      const { company_id } = await getCompanyID();
      if (company_id === 589) {
        // Only add the script if it doesn't already exist
        if (!document.querySelector('script[src="https://www.googletagmanager.com/gtag/js?id=G-0SNJCC3TQ1"]')) {
          const script = document.createElement("script");
          script.async = true;
          script.src = "https://www.googletagmanager.com/gtag/js?id=G-0SNJCC3TQ1";
          script.onload = () => {
            const inlineScript = document.createElement("script");
            inlineScript.innerHTML = `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-0SNJCC3TQ1');
            `;
            document.head.appendChild(inlineScript);
          };
          document.head.appendChild(script);
        }

        const nextivaScript = document.createElement("script");
        nextivaScript.src =
          "https://d4cq8fw7kph8i.cloudfront.net/Simplify360Chat.js?key=Zlp0R0lXbCsySTQ9";
        nextivaScript.async = true;
        document.head.appendChild(nextivaScript);
      } else if (company_id === 601) {
        const script = document.createElement("script");
        script.async = true;
        script.src = "https://www.googletagmanager.com/gtag/js?id=G-XBF1PE4573";
        document.head.appendChild(script);
        const inlineScript = document.createElement("script");
        inlineScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-XBF1PE4573');
        `;
        document.head.appendChild(inlineScript);
      } else if (company_id === 812) {
        // Inject GTM inline initialization script
        if (!document.querySelector('script[data-gtm-init="GTM-N9TSXTD8"]')) {
          const inlineScript = document.createElement("script");
          inlineScript.setAttribute("data-gtm-init", "GTM-N9TSXTD8");
          inlineScript.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-N9TSXTD8');
          `;
          document.head.appendChild(inlineScript);
        }

        // Inject GTM noscript
        if (!document.querySelector('noscript[data-gtm-ns="GTM-N9TSXTD8"]')) {
          const noscript = document.createElement("noscript");
          noscript.setAttribute("data-gtm-ns", "GTM-N9TSXTD8");
          noscript.innerHTML =
            `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-N9TSXTD8" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
          document.body.insertBefore(noscript, document.body.firstChild);
        }

        // Inject Google Ads gtag.js and config for AW-17574660149
        if (!document.querySelector('script[src="https://www.googletagmanager.com/gtag/js?id=AW-17574660149"]')) {
          const gtagScript = document.createElement("script");
          gtagScript.async = true;
          gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=AW-17574660149";
          gtagScript.onload = () => {
            if (!document.querySelector('script[data-gtag-init="AW-17574660149"]')) {
              const inlineGtag = document.createElement("script");
              inlineGtag.setAttribute("data-gtag-init", "AW-17574660149");
              inlineGtag.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'AW-17574660149');
              `;
              document.head.appendChild(inlineGtag);
            }
          };
          document.head.appendChild(gtagScript);
        }
      }
      else if (company_id === 867) {
        if (!document.querySelector('script[src="https://www.googletagmanager.com/gtag/js?id=AW-17866244484"]')) {
          const gtagScript = document.createElement("script");
          gtagScript.async = true;
          gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=AW-17866244484";
          gtagScript.onload = () => {
            if (!document.querySelector('script[data-gtag-init="AW-17866244484"]')) {
              const inlineGtag = document.createElement("script");
              inlineGtag.setAttribute("data-gtag-init", "AW-17866244484");
              inlineGtag.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'AW-17866244484');
              `;
              document.head.appendChild(inlineGtag);
            }
          };
          document.head.appendChild(gtagScript);
        }
      }

      if (company_id) {
        const stripeKey = await fetchStripeKey(company_id);
        if (stripeKey) {
          setStripePromise(loadStripe(stripeKey));
        } else {
          setStripePromise(loadStripe(""));
        }
      } else {
        console.error("Company ID is not available.");
        setStripePromise(loadStripe(""));
        toast.error("Company ID is missing. Unable to initialize Stripe.");
      }

      setLoading(false);
    };

    initializeStripe();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center col-span-full min-h-[100vh]">
        <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
      </div>
    );
  }

  // Move BrowserRouter to the top-level render, not inside RootComponent
  function DataProviderWithNavigate({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const navigate = useNavigate();
    return <DataProvider navigate={navigate}>{children}</DataProvider>;
  }

  return (
    <GoogleOAuthProvider clientId="792199944204-3u7t512ta8hbrhptdjomhqp2nb7jh41h.apps.googleusercontent.com">
      <DataProviderWithNavigate>
        <Elements stripe={stripePromise || loadStripe("")}>
          <App />
        </Elements>
        <Toaster />
      </DataProviderWithNavigate>
    </GoogleOAuthProvider>
  );
};

// Wrap your app with BrowserRouter at the top-level
root.render(
  <BrowserRouter>
    <RootComponent />
  </BrowserRouter>
);

reportWebVitals();
